import { FC } from 'react';
import { IllustEmptyProduct, IllustEmptyWarning, MiniLoader } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import styled from '@emotion/styled';

import {
  ChipAndSortingComponentType,
  ChipAndSortingFilter,
  EmptyViewError,
  FilterParam,
  GrouppedComponentListMap,
  ImageBannerListComponentType,
  LookBookComponentType,
  ProductGroupComponentType,
  TextAndSortingComponentType,
  TextAndSortingFilter,
} from '@domains/storehome-common';

import { ImageBannerListContainer } from '../collection/image-banner-list/containers/ImageBannerListContainer';
import { ImageCardContainer } from '../collection/image-card/containers/ImageCardContainer';
import { FullLookbookContainer } from '../collection/lookbook/containers/FullLookbookContainer';
import { LookbookContainer } from '../collection/lookbook/containers/LookbookContainer';
import { ProductGroupContainer } from '../product-group/containers/ProductGroupContainer';
import { UxProductList } from '../product-list';
import { useComponentListService } from '../services/useComponentListService';

const getUBLObjectSectionByContentType = (type: string) => {
  switch (type) {
    case 'BEST_PRODUCTS':
      return 'best';
    case 'ZIGZIN_PRODUCTS':
      return 'zonly';
    case 'COLLECTION':
      return 'shop_collection_display';
    default:
      return 'all';
  }
};

interface Props {
  type: 'store' | 'category' | 'zigzin-shipping';
  params: {
    shop_id: string;
    category_id?: string | null;
    check_button_item_ids?: string[];
    sub_filter_id_list?: string[];
  };
  components: GrouppedComponentListMap;
  is_fetching: boolean;
  error?: unknown;
  has_next_page?: boolean;
  is_selected?: boolean;
  onFetchNextPage: () => void;
  onParamsChange: (params: FilterParam) => void;
  refetch?: () => void;
}

export const DefaultComponentList: FC<React.PropsWithChildren<Props>> = ({
  type,
  params,
  components,
  is_fetching,
  error,
  has_next_page,
  onFetchNextPage,
  onParamsChange,
  refetch,
}) => {
  const { pageLog, columnCount, setColumnCount, targetRef } = useComponentListService({
    type,
    params,
    has_next_page,
    onFetchNextPage,
  });

  if (error) {
    return <EmptyViewError size='small' retry={refetch} />;
  }

  const {
    PRODUCT_GROUP: product_group_data,
    TEXT_AND_SORT: text_and_sort_data,
    CHIP_AND_SORT: chip_and_sort_data,
    PRODUCT: product_data,
    IMAGE_BANNER_AND_LIST: image_banner_and_list_data,
    LOOKBOOK: lookbook_data,
    FULL_LOOKBOOK: full_lookbook_list_data,
    IMAGE_CARD: image_card_list_data,
  } = components;

  const isEmpty =
    !product_group_data?.length &&
    !product_data?.length &&
    params.check_button_item_ids?.length === 0 &&
    params.sub_filter_id_list?.length === 0;

  if (!is_fetching && isEmpty) {
    return (
      <ErrorWrap fullHeight={type === 'category'}>
        <EmptyViewError
          icon={<IllustEmptyWarning />}
          title='상품이 없어요'
          description={`스토어에서 상품을 준비 중이에요.\n조금만 기다려 주세요!`}
          size='small'
        />
      </ErrorWrap>
    );
  }

  return (
    <ComponentListWrap className='component-list'>
      <div className='default-component-list'>
        {product_group_data &&
          product_group_data.map((product_group, index) => (
            <ProductGroupContainer
              key={`${params.shop_id}_${params.category_id}_${index}`}
              shop_id={params.shop_id}
              category_id={params.category_id || ''}
              component={product_group as ProductGroupComponentType}
              log={
                pageLog && {
                  ...pageLog,
                  object_section: getUBLObjectSectionByContentType(
                    (product_group as ProductGroupComponentType).contents_type,
                  ),
                }
              }
            />
          ))}

        {image_banner_and_list_data &&
          image_banner_and_list_data.map((collection, index) => (
            <ImageBannerListContainer
              key={`${params.shop_id}_${collection.type}_${index}`}
              component={collection as ImageBannerListComponentType}
              log={
                pageLog && {
                  ...pageLog,
                  object_section: 'shop_collection_display',
                }
              }
            />
          ))}

        {image_card_list_data &&
          image_card_list_data.map((collection, index) => (
            <ImageCardContainer
              key={`${params.shop_id}_${collection.type}_${index}`}
              component={collection}
              log={
                pageLog && {
                  ...pageLog,
                  object_section: collection.ubl?.object?.section ?? 'shop_collection_display',
                  object_id: collection.ubl?.object?.id ?? 'shop_collection_display_image',
                  data: {
                    server_log: {
                      ...(collection.ubl?.server_log && JSON.parse(collection.ubl.server_log)),
                      component_idx: (product_group_data?.length ?? 0) + index,
                    },
                  },
                }
              }
            />
          ))}

        {lookbook_data && <LookbookContainer component={lookbook_data[0] as LookBookComponentType} />}

        {full_lookbook_list_data &&
          full_lookbook_list_data.map((collection, index) => (
            <FullLookbookContainer
              key={`${params.shop_id}_${collection.type}_${index}`}
              component={collection}
              log={
                pageLog && {
                  ...pageLog,
                  object_section: collection.ubl?.object?.section ?? 'shop_collection_display',
                  object_id: collection.ubl?.object?.id ?? 'shop_collection_display_image',
                  data: {
                    server_log: {
                      ...(collection.ubl?.server_log && JSON.parse(collection.ubl.server_log)),
                      component_idx: (product_group_data?.length ?? 0) + (image_card_list_data?.length ?? 0) + index,
                    },
                  },
                }
              }
            />
          ))}
      </div>

      {text_and_sort_data && (
        <TextAndSortingFilter
          active_navigation_id={params.category_id}
          component={text_and_sort_data[0] as TextAndSortingComponentType}
          onChangeFilter={onParamsChange}
          log={{
            navigation: params.category_id ? 'shop_category_detail' : 'shop_detail',
            navigation_sub: { shop_id: params.shop_id },
            ...(pageLog && pageLog),
          }}
        />
      )}

      {chip_and_sort_data && (
        <ChipAndSortingFilter
          component={chip_and_sort_data[0] as ChipAndSortingComponentType}
          onChangeFilter={onParamsChange}
          columnCount={columnCount}
          setColumnCount={setColumnCount}
          shopId={params.shop_id}
          log={{
            navigation: params.category_id ? 'shop_category_detail' : 'shop_detail',
            navigation_sub: { shop_id: params.shop_id },
            ...(pageLog && pageLog),
          }}
        />
      )}

      {product_data && product_data.length ? (
        <UxProductList
          component={product_data}
          log={
            pageLog && {
              ...pageLog,
              object_section: 'all',
            }
          }
          use_impression
          columnCount={columnCount}
        />
      ) : (
        !is_fetching && (
          <ErrorWrap>
            <EmptyViewError
              icon={<IllustEmptyProduct />}
              title='조건에 맞는 상품이 없어요'
              description='필터를 수정해 주세요.'
              size='small'
            />
          </ErrorWrap>
        )
      )}

      {has_next_page && (
        <LoadingWrap ref={targetRef}>
          <MiniLoader />
        </LoadingWrap>
      )}
    </ComponentListWrap>
  );
};

const ComponentListWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${vars.color.staticWhite};
  z-index: 10;

  .default-component-list {
    display: flex;
    flex-direction: column;
    gap: 32px;
    &:not(:empty) {
      padding-bottom: 32px;
    }
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 155px;
`;

const ErrorWrap = styled.div<{ fullHeight?: boolean }>`
  position: relative;
  flex: 1;
  min-height: ${({ fullHeight }) => (fullHeight ? 'calc(100vh - 100px)' : '400px')};
  background-color: ${vars.color.backgroundSurface};
  display: flex;
  align-items: center;
`;
